





















































































































































































































import {
  defineComponent,
  PropType,
  ref,
  computed,
  ComputedRef,
  watch,
} from '@vue/composition-api';
import { MarkupUnit, RowTypes } from '@oare/types';
import Row from '@/views/Texts/CollectionTexts/AddTexts/Editor/components/Row.vue';
import { RowWithLine } from '@/views/Texts/CollectionTexts/AddTexts/Editor/components/Column.vue';

interface StartEndChar {
  startChar: number | null;
  endChar: number | null;
}

export default defineComponent({
  props: {
    newSign: {
      type: String,
      required: false,
    },
    existingMarkup: {
      type: Array as PropType<MarkupUnit[]>,
      required: true,
    },
    referenceUuid: {
      type: String,
      required: true,
    },
  },
  components: {
    Row,
  },
  setup(props, { emit }) {
    const damage = ref(
      props.existingMarkup.map(m => m.type).includes('damage')
    );

    const damageChars = ref<StartEndChar[]>(
      props.existingMarkup
        .filter(m => m.type === 'damage')
        .map(m => ({ startChar: m.startChar, endChar: m.endChar }))
        .sort((a, b) => {
          if (a.startChar === null) {
            return -1;
          }
          if (b.startChar === null) {
            return 1;
          }
          return a.startChar - b.startChar;
        })
    );

    const partialDamage = ref(
      props.existingMarkup.map(m => m.type).includes('partialDamage')
    );

    const partialDamageChars = ref<StartEndChar[]>(
      props.existingMarkup
        .filter(m => m.type === 'partialDamage')
        .map(m => ({ startChar: m.startChar, endChar: m.endChar }))
        .sort((a, b) => {
          if (a.startChar === null) {
            return -1;
          }
          if (b.startChar === null) {
            return 1;
          }
          return a.startChar - b.startChar;
        })
    );

    const superfluous = ref(
      props.existingMarkup.map(m => m.type).includes('superfluous')
    );
    const omitted = ref(
      props.existingMarkup.map(m => m.type).includes('omitted')
    );
    const erasure = ref(
      props.existingMarkup.map(m => m.type).includes('erasure')
    );
    const uninterpreted = ref(
      props.existingMarkup.map(m => m.type).includes('isUninterpreted')
    );
    const phoneticComplement = ref(
      props.existingMarkup.map(m => m.type).includes('phoneticComplement')
    );
    const writtenOverErasure = ref(
      props.existingMarkup.map(m => m.type).includes('isWrittenOverErasure')
    );
    const uncertain = ref(
      props.existingMarkup.map(m => m.type).includes('uncertain')
    );
    const writtenBelowTheLine = ref(
      props.existingMarkup.map(m => m.type).includes('isWrittenBelowTheLine')
    );
    const writtenAboveTheLine = ref(
      props.existingMarkup.map(m => m.type).includes('isWrittenAboveTheLine')
    );
    const emended = ref(
      props.existingMarkup.map(m => m.type).includes('isEmendedReading')
    );
    const collated = ref(
      props.existingMarkup.map(m => m.type).includes('isCollatedReading')
    );

    const originalSignRow = ref<RowWithLine>({
      type: 'Line' as RowTypes,
      uuid: '',
      isEditing: false,
      hasErrors: false,
      text: props.existingMarkup.map(m => m.type).includes('originalSign')
        ? props.existingMarkup.find(m => m.type === 'originalSign')
            ?.altReading || undefined
        : undefined,
      line: 0,
    });
    const alternateSignRow = ref<RowWithLine>({
      type: 'Line' as RowTypes,
      uuid: '',
      isEditing: false,
      hasErrors: false,
      text: props.existingMarkup.map(m => m.type).includes('alternateSign')
        ? props.existingMarkup.find(m => m.type === 'alternateSign')
            ?.altReading || undefined
        : undefined,
      line: 0,
    });

    const markupUnits: ComputedRef<MarkupUnit[]> = computed(() => {
      const markupUnits: MarkupUnit[] = [];

      if (damage.value) {
        damageChars.value.forEach(damageChar => {
          markupUnits.push({
            referenceUuid: props.referenceUuid,
            type: 'damage',
            startChar: damageChar.startChar,
            endChar: damageChar.endChar,
            altReading: null,
            altReadingUuid: null,
            value: null,
          });
        });
      }

      if (partialDamage.value) {
        partialDamageChars.value.forEach(partialDamageChar => {
          markupUnits.push({
            referenceUuid: props.referenceUuid,
            type: 'partialDamage',
            startChar: partialDamageChar.startChar,
            endChar: partialDamageChar.endChar,
            altReading: null,
            altReadingUuid: null,
            value: null,
          });
        });
      }

      if (superfluous.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'superfluous',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });
      }

      if (omitted.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'omitted',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });
      }

      if (erasure.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'erasure',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });
      }

      if (uninterpreted.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'isUninterpreted',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });
      }

      if (phoneticComplement.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'phoneticComplement',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });
      }

      if (writtenOverErasure.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'isWrittenOverErasure',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });
      }

      if (uncertain.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'uncertain',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });

        if (
          alternateSignRow.value.signs &&
          alternateSignRow.value.signs.length === 1
        ) {
          markupUnits.push({
            referenceUuid: props.referenceUuid,
            type: 'alternateSign',
            startChar: null,
            endChar: null,
            altReading: alternateSignRow.value.signs[0].reading || null,
            altReadingUuid: alternateSignRow.value.signs[0].readingUuid,
            value: null,
          });
        }
      }

      if (writtenBelowTheLine.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'isWrittenBelowTheLine',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });
      }

      if (writtenAboveTheLine.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'isWrittenAboveTheLine',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });
      }

      if (emended.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'isEmendedReading',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });

        if (
          originalSignRow.value.signs &&
          originalSignRow.value.signs.length === 1
        ) {
          markupUnits.push({
            referenceUuid: props.referenceUuid,
            type: 'originalSign',
            startChar: null,
            endChar: null,
            altReading: originalSignRow.value.signs[0].reading || null,
            altReadingUuid: originalSignRow.value.signs[0].readingUuid,
            value: null,
          });
        }
      }

      if (collated.value) {
        markupUnits.push({
          referenceUuid: props.referenceUuid,
          type: 'isCollatedReading',
          startChar: null,
          endChar: null,
          altReading: null,
          altReadingUuid: null,
          value: null,
        });

        if (
          originalSignRow.value.signs &&
          originalSignRow.value.signs.length === 1
        ) {
          markupUnits.push({
            referenceUuid: props.referenceUuid,
            type: 'originalSign',
            startChar: null,
            endChar: null,
            altReading: originalSignRow.value.signs[0].reading || null,
            altReadingUuid: originalSignRow.value.signs[0].readingUuid,
            value: null,
          });
        }
      }

      return markupUnits;
    });

    watch(markupUnits, () => emit('update-markup', markupUnits.value));

    const damageStartCharOptions = computed(() => {
      const optionArray: number[][] = [];

      damageChars.value.forEach(char => {
        let options: number[] = [];
        for (let i = 1; i < signLength.value; i++) {
          options.push(i);
        }
        if (char.endChar) {
          options = options.filter(option => option < char.endChar!);
        }
        optionArray.push(options);
      });

      return optionArray;
    });

    const partialDamageStartCharOptions = computed(() => {
      const optionArray: number[][] = [];

      partialDamageChars.value.forEach(char => {
        let options: number[] = [];
        for (let i = 1; i < signLength.value; i++) {
          options.push(i);
        }
        if (char.endChar) {
          options = options.filter(option => option < char.endChar!);
        }
        optionArray.push(options);
      });

      return optionArray;
    });

    const damageEndCharOptions = computed(() => {
      const optionArray: number[][] = [];

      damageChars.value.forEach(char => {
        let options: number[] = [];
        for (let i = 1; i < signLength.value; i++) {
          options.push(i);
        }
        if (char.startChar) {
          options = options.filter(option => option > char.startChar!);
        }
        optionArray.push(options);
      });

      return optionArray;
    });

    const partialDamageEndCharOptions = computed(() => {
      const optionArray: number[][] = [];

      partialDamageChars.value.forEach(char => {
        let options: number[] = [];
        for (let i = 1; i < signLength.value; i++) {
          options.push(i);
        }
        if (char.startChar) {
          options = options.filter(option => option > char.startChar!);
        }
        optionArray.push(options);
      });

      return optionArray;
    });

    watch(damage, () => {
      if (damage.value) {
        damageChars.value.push({
          startChar: null,
          endChar: null,
        });
      } else {
        damageChars.value = [];
      }
    });

    watch(partialDamage, () => {
      if (partialDamage.value) {
        partialDamageChars.value.push({
          startChar: null,
          endChar: null,
        });
      } else {
        partialDamageChars.value = [];
      }
    });

    const addDamage = () => {
      damageChars.value.push({
        startChar: null,
        endChar: null,
      });
    };

    const removeDamage = (index: number) => {
      damageChars.value.splice(index, 1);
    };

    const addPartialDamage = () => {
      partialDamageChars.value.push({
        startChar: null,
        endChar: null,
      });
    };

    const removePartialDamage = (index: number) => {
      partialDamageChars.value.splice(index, 1);
    };

    const signLength = computed(
      () =>
        props.newSign
          ?.replace(/([[\]{}⸢⸣«»‹›:;*?\\!])|(".+")|('.+')|(^\/)+/g, '')
          .replace(/<[^>]*>/g, '')
          .replace(/\([^()]*\)/g, '').length || 0
    );

    return {
      damage,
      partialDamage,
      superfluous,
      omitted,
      erasure,
      uninterpreted,
      phoneticComplement,
      writtenOverErasure,
      uncertain,
      writtenBelowTheLine,
      writtenAboveTheLine,
      emended,
      collated,
      originalSignRow,
      alternateSignRow,
      damageStartCharOptions,
      partialDamageStartCharOptions,
      damageEndCharOptions,
      partialDamageEndCharOptions,
      damageChars,
      addDamage,
      removeDamage,
      partialDamageChars,
      addPartialDamage,
      removePartialDamage,
      signLength,
    };
  },
});
